/**
 * @file Route-local Compliance register state, paging, and stale-load guards.
 */

import {
    listComplianceRecordHistory,
    listComplianceRequirements,
    listComplianceServiceHistory,
    listComplianceSummaries,
} from "./complianceApi.js";

function createPageState() {
    return {
        entries: [],
        cursor: null,
        hasMore: true,
        loading: false,
        error: null,
        sourceSummary: null,
    };
}

function createRequirementState() {
    return {
        active: createPageState(),
        inactive: createPageState(),
    };
}

const complianceState = {
    generation: 0,
    loadState: "idle",
    error: null,
    configured: false,
    categories: [],
    limits: null,
    showInactive: false,
    summaries: {
        active: createPageState(),
        inactive: createPageState(),
    },
    expandedItems: new Set(),
    expandedRequirements: new Set(),
    requirements: new Map(),
    histories: new Map(),
    focusKey: "",
};

function resetRouteCollections() {
    complianceState.showInactive = false;
    complianceState.summaries = {
        active: createPageState(),
        inactive: createPageState(),
    };
    complianceState.expandedItems = new Set();
    complianceState.expandedRequirements = new Set();
    complianceState.requirements = new Map();
    complianceState.histories = new Map();
    complianceState.focusKey = "";
}

function mergeEntries(existing, incoming) {
    const byId = new Map(existing.map((entry) => [entry.id, entry]));
    incoming.forEach((entry) => {
        if (entry?.id) {byId.set(entry.id, entry);}
    });
    return [...byId.values()];
}

function requirementKey(itemId, requirementId) {
    return `${itemId}:${requirementId}`;
}

function compareStrings(left, right) {
    return String(left ?? "").localeCompare(String(right ?? ""));
}

function compareComplianceSummaries(left, right) {
    const rankDifference = Number(left?.summaryUrgencyRank) -
        Number(right?.summaryUrgencyRank);
    if (rankDifference !== 0) {return rankDifference;}
    const dueDifference = compareStrings(
        left?.summaryDueDateSort,
        right?.summaryDueDateSort,
    );
    if (dueDifference !== 0) {return dueDifference;}
    const nameDifference = compareStrings(
        left?.nameSortKey,
        right?.nameSortKey,
    );
    if (nameDifference !== 0) {return nameDifference;}
    return compareStrings(left?.id, right?.id);
}

function compareComplianceRequirements(left, right) {
    const rankDifference = Number(left?.currentUrgencyRank) -
        Number(right?.currentUrgencyRank);
    if (rankDifference !== 0) {return rankDifference;}
    const dueDifference = compareStrings(
        left?.currentDueDateSort,
        right?.currentDueDateSort,
    );
    if (dueDifference !== 0) {return dueDifference;}
    const nameDifference = compareStrings(
        left?.nameSortKey,
        right?.nameSortKey,
    );
    if (nameDifference !== 0) {return nameDifference;}
    return compareStrings(left?.id, right?.id);
}

/**
 * Returns the mutable Compliance route state.
 * @returns {object} Route state.
 */
export function getComplianceState() {
    return complianceState;
}

/**
 * Starts a fresh guarded route session.
 * @returns {number} Route generation token.
 */
export function beginComplianceRouteSession() {
    complianceState.generation += 1;
    complianceState.loadState = "loading";
    complianceState.error = null;
    complianceState.configured = false;
    complianceState.categories = [];
    complianceState.limits = null;
    resetRouteCollections();
    return complianceState.generation;
}

/**
 * Returns whether an async result still belongs to the active route session.
 * @param {number} generation Captured route generation.
 * @returns {boolean} True when current.
 */
export function isComplianceGenerationCurrent(generation) {
    return complianceState.generation === generation;
}

/**
 * Tears down Compliance route state and invalidates pending reads.
 * @returns {void}
 */
export function resetComplianceRouteState() {
    complianceState.generation += 1;
    complianceState.loadState = "idle";
    complianceState.error = null;
    complianceState.configured = false;
    complianceState.categories = [];
    complianceState.limits = null;
    resetRouteCollections();
}

/**
 * Stores the authorized bootstrap for the active route generation.
 * @param {number} generation Route generation.
 * @param {object} bootstrap Callable response.
 * @returns {boolean} Whether the bootstrap was accepted.
 */
export function applyComplianceBootstrap(generation, bootstrap) {
    if (!isComplianceGenerationCurrent(generation)) {return false;}
    complianceState.configured = bootstrap?.configured === true;
    complianceState.categories = Array.isArray(bootstrap?.categories) ?
        bootstrap.categories : [];
    complianceState.limits = bootstrap?.limits || null;
    complianceState.loadState = "ready";
    complianceState.error = null;
    return true;
}

/**
 * Records a route-level startup failure.
 * @param {number} generation Route generation.
 * @param {Error} error Startup error.
 * @returns {boolean} Whether the error was accepted.
 */
export function applyComplianceStartupError(generation, error) {
    if (!isComplianceGenerationCurrent(generation)) {return false;}
    complianceState.loadState = "error";
    complianceState.error = error;
    return true;
}

/**
 * Loads one bounded active or inactive tracked-item page.
 * @param {number} generation Route generation.
 * @param {"active"|"inactive"} view Page view.
 * @returns {Promise<boolean>} Whether current state changed.
 */
export async function loadComplianceSummaryPage(generation, view) {
    const page = complianceState.summaries[view];
    if (!page || page.loading || !page.hasMore ||
        !isComplianceGenerationCurrent(generation)) {return false;}
    page.loading = true;
    page.error = null;
    try {
        const response = await listComplianceSummaries({
            view,
            cursor: page.cursor,
        });
        if (!isComplianceGenerationCurrent(generation)) {return false;}
        page.entries = mergeEntries(
            page.entries,
            Array.isArray(response?.items) ? response.items : [],
        ).sort(compareComplianceSummaries);
        page.cursor = response?.nextCursor || null;
        page.hasMore = response?.hasMore === true;
        return true;
    } catch (error) {
        if (!isComplianceGenerationCurrent(generation)) {return false;}
        page.error = error;
        return true;
    } finally {
        if (isComplianceGenerationCurrent(generation)) {
            page.loading = false;
        }
    }
}

/**
 * Loads one bounded requirement page for a tracked item.
 * @param {number} generation Route generation.
 * @param {string} itemId Tracked item ID.
 * @param {"active"|"inactive"} view Requirement view.
 * @returns {Promise<boolean>} Whether current state changed.
 */
export async function loadComplianceRequirementPage(
    generation,
    itemId,
    view,
) {
    if (!isComplianceGenerationCurrent(generation)) {return false;}
    let state = complianceState.requirements.get(itemId);
    if (!state) {
        state = createRequirementState();
        complianceState.requirements.set(itemId, state);
    }
    const page = state[view];
    if (!page || page.loading || !page.hasMore) {return false;}
    page.loading = true;
    page.error = null;
    try {
        const response = await listComplianceRequirements({
            itemId,
            view,
            cursor: page.cursor,
        });
        if (!isComplianceGenerationCurrent(generation)) {return false;}
        page.entries = mergeEntries(
            page.entries,
            Array.isArray(response?.requirements) ?
                response.requirements : [],
        ).sort(compareComplianceRequirements);
        page.cursor = response?.nextCursor || null;
        page.hasMore = response?.hasMore === true;
        return true;
    } catch (error) {
        if (!isComplianceGenerationCurrent(generation)) {return false;}
        page.error = error;
        return true;
    } finally {
        if (isComplianceGenerationCurrent(generation)) {
            page.loading = false;
        }
    }
}

/**
 * Loads one bounded record-history page for a requirement.
 * @param {number} generation Route generation.
 * @param {string} itemId Tracked item ID.
 * @param {string} requirementId Requirement ID.
 * @param {"manual"|"maintenance_service"} sourceType Requirement source.
 * @returns {Promise<boolean>} Whether current state changed.
 */
export async function loadComplianceHistoryPage(
    generation,
    itemId,
    requirementId,
    sourceType = "manual",
) {
    if (!isComplianceGenerationCurrent(generation)) {return false;}
    const key = requirementKey(itemId, requirementId);
    let page = complianceState.histories.get(key);
    if (!page) {
        page = createPageState();
        complianceState.histories.set(key, page);
    }
    if (page.loading || !page.hasMore) {return false;}
    page.loading = true;
    page.error = null;
    try {
        const sourceHistory = sourceType === "maintenance_service";
        const response = await (sourceHistory ?
            listComplianceServiceHistory :
            listComplianceRecordHistory)({
            itemId,
            requirementId,
            cursor: page.cursor,
        });
        if (!isComplianceGenerationCurrent(generation)) {return false;}
        page.entries = mergeEntries(
            page.entries,
            sourceHistory ?
                (Array.isArray(response?.services) ? response.services : []) :
                (Array.isArray(response?.records) ? response.records : []),
        );
        page.sourceSummary = response?.sourceSummary ||
            page.sourceSummary ||
            null;
        page.cursor = response?.nextCursor || null;
        page.hasMore = response?.hasMore === true;
        return true;
    } catch (error) {
        if (!isComplianceGenerationCurrent(generation)) {return false;}
        page.error = error;
        return true;
    } finally {
        if (isComplianceGenerationCurrent(generation)) {
            page.loading = false;
        }
    }
}

/**
 * Returns one item's active/inactive requirement pages.
 * @param {string} itemId Tracked item ID.
 * @returns {object|null} Requirement state.
 */
export function getComplianceRequirementState(itemId) {
    return complianceState.requirements.get(itemId) || null;
}

/**
 * Returns one requirement history page.
 * @param {string} itemId Tracked item ID.
 * @param {string} requirementId Requirement ID.
 * @returns {object|null} History page.
 */
export function getComplianceHistoryState(itemId, requirementId) {
    return complianceState.histories.get(
        requirementKey(itemId, requirementId),
    ) || null;
}

/**
 * Upserts an authoritative tracked item into the correct board page.
 * @param {object|null} item Authoritative item, or null after delete.
 * @param {string} [deletedItemId] Deleted identity.
 * @returns {void}
 */
export function reconcileComplianceItem(item, deletedItemId = "") {
    const identity = item?.id || deletedItemId;
    if (!identity) {return;}
    ["active", "inactive"].forEach((view) => {
        const page = complianceState.summaries[view];
        page.entries = page.entries.filter((entry) => entry.id !== identity);
    });
    if (item) {
        const view = item.isActive === false ? "inactive" : "active";
        complianceState.summaries[view].entries.push(item);
        complianceState.summaries[view].entries.sort(
            compareComplianceSummaries,
        );
    } else {
        complianceState.requirements.delete(identity);
        for (const key of complianceState.histories.keys()) {
            if (key.startsWith(`${identity}:`)) {
                complianceState.histories.delete(key);
            }
        }
    }
}

/**
 * Upserts or removes an authoritative requirement and its parent item.
 * @param {string} itemId Parent item ID.
 * @param {object|null} requirement Authoritative requirement.
 * @param {object|null} item Authoritative parent item.
 * @param {string} [deletedRequirementId] Deleted identity.
 * @returns {void}
 */
export function reconcileComplianceRequirement(
    itemId,
    requirement,
    item,
    deletedRequirementId = "",
) {
    if (item) {reconcileComplianceItem(item);}
    let state = complianceState.requirements.get(itemId);
    if (!state) {
        state = createRequirementState();
        complianceState.requirements.set(itemId, state);
    }
    const identity = requirement?.id || deletedRequirementId;
    if (!identity) {return;}
    ["active", "inactive"].forEach((view) => {
        state[view].entries = state[view].entries
            .filter((entry) => entry.id !== identity);
    });
    if (requirement) {
        const view = requirement.isActive === false ? "inactive" : "active";
        state[view].entries.push(requirement);
        state[view].entries.sort(compareComplianceRequirements);
    } else {
        complianceState.histories.delete(requirementKey(itemId, identity));
    }
}

/**
 * Invalidates a requirement's history after a record mutation.
 * @param {string} itemId Parent item ID.
 * @param {string} requirementId Requirement ID.
 * @returns {void}
 */
export function invalidateComplianceHistory(itemId, requirementId) {
    complianceState.histories.delete(requirementKey(itemId, requirementId));
}
